import { createContext, useEffect, useState } from "react";
import {
  localStorageRemove,
  localStorageSave,
} from "../../../services/localStorageService";
import PropTypes from "prop-types";
import {
  createAircraft,
  createType,
  getAircraftList,
  getTypeSeries,
  removeAircraft,
  removeType,
} from "../services/AircraftConfigurationService";
import { useSnackBar } from "../../../hooks/useSnackBar";
import { HTTP_RESPONSE_CODES } from "../../../globals/globals";

const AircraftConfigurationContext = createContext();

const localStoragePrefix = "configuration/aircraft/generalInfo/draft";

const AircraftConfigurationProvider = ({
  aircraftId,
  children,
  newAircraft,
}) => {
  const [aircraftList, setAircraftList] = useState([]);
  const [idxList, setIdxList] = useState({});
  const [drafts, setDrafts] = useState(getSectionsByPrefix(localStoragePrefix));
  const [filters, setFilters] = useState({ dropdowns: {}, txt: {} });
  const [idxListBackup, setIdxListBackup] = useState({});
  const [listChange, setListChange] = useState({});
  const [newDrafts, setNewDrafts] = useState({});
  const [loadingAircraft, setLoadingAircraft] = useState(false);
  const [maxPages, setMaxPages] = useState(null);
  const [page, setPage] = useState(0);
  const { successMsg, warningMsg, infoMsg } = useSnackBar();
  const [sortingOrder, setSortingOrder] = useState("tailNumber");
  const pageSize = 10;
  const onFiltersChange = (dropdownFilters, txtFilters) => {
    setFilters({
      dropdowns: dropdownFilters,
      txt: txtFilters,
    });
  };

  const confirmAircraft = (aircraftId) => {
    setIdxListBackup((prev) => ({
      ...prev,
      [aircraftId]: idxList[aircraftId],
    }));
    
    removeDraft(aircraftId, "row");
  };
  const rowUpdater = {
    general: {
      operationalStatus: (value) => ({ operationalStatus: value.name }),
      companyId: (value) => ({ companyName: value.name }),
    },
  };
  const updateSection = (formValue, aircraftId, section) => {
    let { name, value } = formValue;
    updateRow(
      aircraftId,
      rowUpdater[section]?.[name]?.(value) ?? { [name]: value }
    );
  };
  const updateValue = (aircraftId, name, value) => {
    if (name === "operationalStatus") {
      value = value.name;
    }

    setIdxList((prev) => ({
      ...prev,
      [aircraftId]: { ...prev[aircraftId], [name]: value },
    }));
  };

  const resetType = (aircraftId) => {
    infoMsg("Draft discarded");
    if (aircraftId in newDrafts) {
      const backup = idxListBackup[aircraftId];
      if (backup !== undefined && "version" in backup) {
        setIdxList((prev) => ({
          ...prev,
          [aircraftId]: backup,
        }));

        setAircraftList((prev) =>
          prev.map((item) => (item.id === aircraftId ? backup : item))
        );
      } else {
        setIdxList((prev) => {
          const { [aircraftId]: _, ...rest } = prev;
          return rest;
        });
  
        setAircraftList((prev) =>
          prev.filter((item) => item.id !== aircraftId)
        );
      }
      removeDraft(aircraftId, "row");
    } else {
      removeDraft(aircraftId, "row");
    }
  };

  useEffect(() => {
    if (!newAircraft || !newAircraft.id) return;

    setIdxList((prev) => ({
      ...prev,
      [newAircraft.id]: newAircraft,
    }));
    saveDraft(newAircraft.id, "general", newAircraft);
    saveDraft(newAircraft.id, "row", {
      id: newAircraft.id,
      operationalStatus: "Active",
      typeSeriesId: aircraftId,
    });
    setNewDrafts((prev) => ({
      ...prev,
      [newAircraft.id]: { id: newAircraft.id, operationalStatus: "Active" },
    }));

    setAircraftList((prev) => [newAircraft, ...prev]);
    setListChange((prev) => ({ ...prev, [newAircraft.id]: "create" }));
  }, [newAircraft]);

  useEffect(() => {
    setLoadingAircraft(true);
    getAircraftList(page, pageSize, aircraftId, sortingOrder)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
          setMaxPages(response.data.totalPages);
          let rowDrafts = getObjectsBySection(localStoragePrefix, "row");
          let backendItems = response.data.load;
          let newItems = Object.fromEntries(
            Object.entries(rowDrafts).filter(
              ([draftId, draft]) =>
                !backendItems.some((item) => item.id === draftId) &&
                draft.typeSeriesId == aircraftId
            )
          );
          setNewDrafts(newItems);
          let idxListMerged = backendItems.reduce((acc, current) => {
            const base = rowDrafts[current.id] ?? current;
            return {
              ...acc,
              [current.id]: base,
            };
          }, {});
          setIdxList((prev) =>
            page > 0
              ? { ...prev, ...idxListMerged }
              : { ...newItems, ...idxListMerged }
          );
          let idxListBackupData = backendItems.reduce(
            (acc, current) => ({
              ...acc,
              [current.id]: current,
            }),
            {}
          );
          setIdxListBackup((prev) =>
            page > 0
              ? { ...prev, ...idxListBackupData }
              : { ...newItems, ...idxListBackupData }
          );
          let displayList = backendItems.map(
            (item) => rowDrafts[item.id] ?? item
          );

          let displayIds = new Set(displayList.map((i) => i.id));
          let onlyNewDrafts = Object.values(newItems).filter(
            (item) => !displayIds.has(item.id)
          );

          setAircraftList((prev) =>
            page > 0
              ? [...prev, ...onlyNewDrafts, ...displayList]
              : [...onlyNewDrafts, ...displayList]
          );
        }
        setLoadingAircraft(false);
      })
      .catch((error) => setLoadingAircraft(false));
  }, [filters, page, sortingOrder]);

  const requestNextPage = () => {
    setPage((prev) => Math.min(prev + 1, maxPages - 1));
  };
  // Draft methods
  const saveDraft = (aircraftId, section, value) => {
    localStorageSave(
      `${localStoragePrefix}/${section}`,
      { id: aircraftId },
      value
    );
    setDrafts((prev) => ({
      ...prev,
      [aircraftId]: (prev[aircraftId] ?? new Set()).add(section),
    }));
  };
  const isAircraftDraft = (id) => id in drafts;
  const isAircraftSectionDraft = (id, section) => drafts[id]?.has(section);
  const removeDraft = (aircraftId, section) => {
    localStorageRemove(`${localStoragePrefix}/${section}`, {
      id: aircraftId.toString(),
    });

    setDrafts((prev) => {
      // Copia el estado actual
      let prevDrafts = { ...prev };

      // Si existe el aircraftId, actualizamos sus secciones
      if (prevDrafts[aircraftId]) {
        let prevSet = new Set(prevDrafts[aircraftId]); // Clonamos el Set actual
        prevSet.delete(section); // Eliminamos la sección especificada

        if (prevSet.size > 0) {
          prevDrafts[aircraftId] = prevSet; // Actualizamos solo si quedan secciones
        } else {
          delete prevDrafts[aircraftId]; // Eliminamos el aircraftId si ya no tiene secciones
        }
      }

      return prevDrafts; // Retornamos el estado actualizado
    });
  };

  const deleteAircraft = (aircraftId) => {
    let isNewDraft = aircraftId in newDrafts;
    if (isNewDraft) {
      resetType(aircraftId);
      removeDraft(aircraftId, "general");
    }
    removeAircraft(aircraftId)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.DELETED) {
          successMsg("Aircraft deleted successfully");
          removeDraft(aircraftId, "general");

          setIdxList((prev) => {
            let copy = { ...prev };
            delete copy[aircraftId];
            return copy;
          });
          setIdxListBackup((prev) => {
            let copy = { ...prev };
            delete copy[aircraftId];
            return copy;
          });
          
          setAircraftList((prev) =>
            prev.filter((part) => part != null && part.id !== aircraftId)
          );

          setListChange((prev) => ({ ...prev, [aircraftId]: "delete" }));
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            100000
          );
        }
      })
      .catch((error) => warningMsg(`Something went wrong: ${error}`));
  };

  const confirmType = (aircraftId) => {
    setIdxListBackup((prev) => ({
      ...prev,
      [aircraftId]: idxList[aircraftId],
    }));
    removeDraft(aircraftId, "row");
    setListChange((prev) => ({ ...prev, [aircraftId]: "update" }));
  };

  const updateRow = (id, partialValue) => {
    const updateState = (prev) => ({
      ...prev,
      [id]: { ...prev[id], ...partialValue },
    });

    setIdxList((prev) => {
      if (isAircraftDraft(id)) {
        saveDraft(id, "row", updateState(prev)[id]);
      }
      return updateState(prev);
    });

    setNewDrafts(updateState);
  };

  return (
    <AircraftConfigurationContext.Provider
      value={{
        idxList,
        updateRow,
        isAircraftDraft,
        isAircraftSectionDraft,
        saveDraft,
        removeDraft,
        deleteAircraft,
        onFiltersChange,
        aircraftList,
        requestNextPage,
        loadingAircraft,
        updateSection,
        updateValue,
        resetType,
        confirmAircraft,
        newDrafts,
        drafts,
        confirmType,
        newAircraft,
        listChange,
        setSortingOrder
      }}
    >
      {children}
    </AircraftConfigurationContext.Provider>
  );
};
function getSectionsByPrefix(prefix) {
  return [...Array(localStorage.length).keys()]
    .map((i) => localStorage.key(i))
    .filter((key) => key.startsWith(prefix))
    .reduce((acc, key) => {
      const queryParams = new URLSearchParams(key.split("?")[1]);
      if (localStorage.getItem("u_id") === queryParams.get("userId")) {
        let id = queryParams.get("id");
        if (!acc[id]) {
          acc[id] = new Set();
        }
        acc[id].add(key.split("?")[0].replace(prefix + "/", ""));
      }
      return acc;
    }, {});
}

function getObjectsBySection(prefix, section) {
  return [...Array(localStorage.length).keys()]
    .map((i) => localStorage.key(i))
    .filter((key) => key.startsWith(prefix + "/" + section))
    .reduce((acc, key) => {
      const queryParams = new URLSearchParams(key.split("?")[1]);
      if (localStorage.getItem("u_id") === queryParams.get("userId")) {
        const id = queryParams.get("id");
        acc[id] = JSON.parse(localStorage.getItem(key));
      }
      return acc;
    }, {});
}

export { AircraftConfigurationContext, AircraftConfigurationProvider };
